import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import { BaseBox } from "../util/BaseBox";
import { BorderBottomTypography } from "../util/BorderBottomTypography";
import { ImageViewer, ImageViewerBottom } from "./ImageView";
import { ScanMainImageSP } from "./ScanMainImage";
import { CollectAndDiagnoseMainImageSP } from "./CollectAndDiagnoseMainImage";
import { ReportAndCareMainImageSP } from "./ReportAndCareMainImage";
import WalccareIcon from "./WalccareIcon";

interface SectionContentProps {
    sectionKey: string;
    mobileImageComponent?: React.ComponentType<any>;
    basePadding?: string;
    basePaddingMobile?: string;
    basePaddingBottom?: string;
}

export default function SectionContent({
    sectionKey,
    mobileImageComponent: MobileImageComponent,
    basePadding = "105.5px 0px 100px 0px",
    basePaddingMobile = "46px",
    basePaddingBottom = "0px",
}: SectionContentProps) {
    const theme = useTheme();
    const { t } = useTranslation("conditionagent");
    const isDownSm = useMediaQuery(theme.breakpoints.down("sm"));

    const sectionData = t(sectionKey, { returnObjects: true }) as any;

    if (!sectionData || !sectionData.icons) {
        return null;
    }

    const getMobileImageComponent = () => {
        switch (sectionKey) {
            case "scan":
                return ScanMainImageSP;
            case "collectAndDiagnose":
                return CollectAndDiagnoseMainImageSP;
            case "reportAndCare":
                return ReportAndCareMainImageSP;
            default:
                return ScanMainImageSP;
        }
    };

    const MobileImage = MobileImageComponent || getMobileImageComponent();

    return (
        <BaseBox
            sx={{
                padding: basePadding,
                [theme.breakpoints.down("sm")]: {
                    paddingTop: basePaddingMobile,
                    paddingBottom: basePaddingBottom,
                },
            }}
        >
            <BorderBottomTypography containerStyles={{}}>{sectionData.title}</BorderBottomTypography>
            {isDownSm ? (
                <Box>
                    <Box display="flex" paddingTop="28px">
                        <Box
                            sx={{
                                width: "15px",
                                backgroundColor: "rgba(167, 176, 177, 0.13)",
                                borderTopLeftRadius: "15px",
                            }}
                        ></Box>
                        <ImageViewer
                            id={`${sectionKey}Image`}
                            scrollPosition={sectionKey === "collectAndDiagnose" ? "center" : "start"}
                        >
                            <MobileImage
                                sx={{
                                    height: "100%",
                                    width: "auto",
                                    maxWidth: "none",
                                    verticalAlign: "bottom",
                                }}
                            />
                        </ImageViewer>
                        <Box
                            sx={{
                                width: "15px",
                                backgroundColor: "rgba(167, 176, 177, 0.13)",
                                borderTopRightRadius: "15px",
                            }}
                        ></Box>
                    </Box>
                    <ImageViewerBottom></ImageViewerBottom>
                </Box>
            ) : (
                <Box display="none"></Box>
            )}
            <Typography
                component="h2"
                variant="h2"
                sx={{
                    padding: "20px 0px 30px 0",
                    [theme.breakpoints.down("sm")]: {
                        padding: "20px 0px 20px 0",
                    },
                }}
            >
                {sectionData.subtitle}
            </Typography>
            <Typography
                component="p"
                variant="body1"
                sx={{
                    paddingBottom: "27px",
                    [theme.breakpoints.down("sm")]: {
                        paddingBottom: "62px",
                    },
                }}
            >
                {sectionData.body}
            </Typography>
            {!isDownSm && sectionData.mainImage ? (
                <img
                    src={sectionData.mainImage}
                    alt="Description"
                    style={{
                        width: "100%",
                        height: "auto",
                        paddingBottom: "69px",
                    }}
                />
            ) : (
                <Box display="none"></Box>
            )}
            <Grid container rowSpacing={{ xs: 38 / 8, lg: 87 / 8 }} columnSpacing={110 / 8}>
                {sectionData.icons.map((icon: any, index: number) => (
                    <Grid item lg={6} xs={12} key={index}>
                        <WalccareIcon t={t} textPath={`${sectionKey}.icons`} i={index} img={icon.image} />
                    </Grid>
                ))}
            </Grid>
        </BaseBox>
    );
}
